import { loadStripe } from '@stripe/stripe-js'

let stripePromise = null

function getStripe() {
  if (!stripePromise) {
    const key = document.querySelector('meta[name="stripe-publishable-key"]')?.content
    if (!key) return null
    stripePromise = loadStripe(key)
  }
  return stripePromise
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

async function stripeRequest(path, options = {}) {
  const resp = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken()
    },
    ...options
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Request failed')
  return data
}

function showError(container, message) {
  container.innerHTML = ''
  const alert = document.createElement('div')
  alert.className = 'alert alert-danger'
  alert.textContent = message
  container.appendChild(alert)
}

// ── Order form: Stripe PaymentElement for plan signup ──

async function initOrderFormStripe() {
  const form = document.getElementById('subscription-form') || document.getElementById('new_order')
  if (!form) return

  const stripe = await getStripe()
  if (!stripe) return

  const container = document.getElementById('stripe-payment-element')
  if (!container) return

  let elements = null
  let paymentElementMounted = false

  async function mountPaymentElement() {
    if (paymentElementMounted) return
    container.innerHTML = '<p class="text-muted small">Loading payment form...</p>'

    try {
      // Check for existing payment methods
      try {
        const pmData = await stripeRequest('/stripe/payment_methods')
        const methods = pmData.payment_methods
        if (methods && methods.length > 0) {
          const desc = describePaymentMethod(methods[0])
          if (desc) {
            container.innerHTML = ''
            const info = document.createElement('div')
            info.className = 'alert alert-success small mb-0'
            info.innerHTML = `<i class="fas fa-check-circle me-1"></i> <strong>${desc.name}</strong> ending in ${desc.last4} will be charged. To use a different payment method, visit the <a href="/stripe/billing_portal">billing portal</a> first.`
            container.appendChild(info)
            paymentElementMounted = true // skip PaymentElement, use existing method
            return
          }
        }
      } catch (e) {
        // No payment methods or not authenticated — show PaymentElement
      }

      const { client_secret } = await stripeRequest('/stripe/setup_intent', { method: 'POST' })
      elements = stripe.elements({ clientSecret: client_secret })
      const paymentElement = elements.create('payment', {
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never'
          }
        },
        wallets: {
          applePay: 'never',
          googlePay: 'never'
        }
      })
      container.innerHTML = ''
      paymentElement.mount(container)
      paymentElementMounted = true
    } catch (err) {
      showError(container, err.message)
    }
  }

  function unmountPaymentElement() {
    if (!paymentElementMounted) return
    container.innerHTML = ''
    elements = null
    paymentElementMounted = false
  }

  // Watch plan radio buttons — mount/unmount based on plan type
  const planRadios = form.querySelectorAll('.plan-radio')
  planRadios.forEach(radio => {
    radio.addEventListener('change', () => {
      if (radio.dataset.planType === 'stripe') {
        mountPaymentElement()
      } else {
        unmountPaymentElement()
      }
    })
  })

  // Check initial state
  const selectedPlan = form.querySelector('.plan-radio:checked')
  if (selectedPlan?.dataset.planType === 'stripe') {
    mountPaymentElement()
  }

  // Intercept form submission for Stripe plans
  form.addEventListener('submit', async (e) => {
    const selected = form.querySelector('.plan-radio:checked')
    if (!selected || selected.dataset.planType !== 'stripe') return // normal submit

    e.preventDefault()
    const submitBtn = form.querySelector('[type="submit"]')
    const originalText = submitBtn.textContent
    submitBtn.disabled = true
    submitBtn.textContent = 'Processing...'

    // Clear previous errors
    const existingError = form.querySelector('.stripe-error')
    if (existingError) existingError.remove()

    try {
      // Guard: payment form must be ready
      if (!paymentElementMounted) {
        throw new Error('Payment form is still loading. Please wait and try again.')
      }

      // 1. Confirm the SetupIntent if using a new payment method (skip if using existing)
      if (elements) {
        const userName = document.querySelector('meta[name="current-user-name"]')?.content
        const userEmail = document.querySelector('meta[name="current-user-email"]')?.content
        const { error } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                name: userName || undefined,
                email: userEmail || undefined
              }
            }
          },
          redirect: 'if_required'
        })

        if (error) {
          throw new Error(error.message)
        }
      }

      // 2. Create the subscription via our API
      const planId = selected.value
      const billingPeriod = form.querySelector('input[name="billing_period"]:checked')?.value
      const body = { subscription_plan_id: planId }
      if (billingPeriod) body.billing_period = billingPeriod

      const result = await stripeRequest('/stripe/subscriptions', {
        method: 'POST',
        body: JSON.stringify(body)
      })

      // 3. Redirect to dashboard
      window.location.href = '/users/me?tab=keys'
    } catch (err) {
      const errorDiv = document.createElement('div')
      errorDiv.className = 'alert alert-danger stripe-error mt-3'
      errorDiv.textContent = err.message
      submitBtn.closest('.card-body')?.prepend(errorDiv)
      submitBtn.disabled = false
      submitBtn.textContent = originalText
    }
  })
}

// ── Billing tab: payment methods, invoices, subscription management ──

async function initBillingTab() {
  const billingTab = document.getElementById('billing-tab-content')
  if (!billingTab) return

  loadPaymentMethods()
  loadUpcomingInvoices()
  loadInvoices()
}

// Display fields for the payment method types /stripe/payment_methods returns
// (card, us_bank_account, sepa_debit).
function describePaymentMethod(pm) {
  if (pm.card) {
    const card = pm.card
    return {
      icon: 'fa-credit-card',
      name: card.brand.charAt(0).toUpperCase() + card.brand.slice(1),
      last4: card.last4,
      detail: `Exp ${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`,
    }
  }
  if (pm.us_bank_account) {
    const bank = pm.us_bank_account
    return {
      icon: 'fa-university',
      name: bank.bank_name || 'Bank account',
      last4: bank.last4,
      detail: bank.account_type ? `${bank.account_type.charAt(0).toUpperCase() + bank.account_type.slice(1)} account` : null,
    }
  }
  if (pm.sepa_debit) {
    const sepa = pm.sepa_debit
    return {
      icon: 'fa-university',
      name: 'SEPA Direct Debit',
      last4: sepa.last4,
      detail: sepa.country || null,
    }
  }
  return null
}

async function loadPaymentMethods() {
  const container = document.getElementById('billing-payment-methods')
  if (!container) return

  try {
    const data = await stripeRequest('/stripe/payment_methods')
    const methods = data.payment_methods

    if (!methods || methods.length === 0) {
      container.innerHTML = '<p class="text-muted small mb-0">No payment methods on file.</p>'
      return
    }

    const html = methods.map(pm => {
      const desc = describePaymentMethod(pm)
      if (!desc) return ''
      return `
        <div class="d-flex align-items-center py-2 border-bottom">
          <i class="fas ${desc.icon} me-2"></i>
          <strong>${desc.name}</strong>
          &nbsp;ending in ${desc.last4}
          ${desc.detail ? `<span class="text-muted small ms-2">${desc.detail}</span>` : ''}
        </div>
      `
    }).join('')

    container.innerHTML = html
  } catch (err) {
    showError(container, err.message)
  }
}

function escapeHtml(value) {
  const div = document.createElement('div')
  div.textContent = value == null ? '' : String(value)
  return div.innerHTML
}

function formatMoney(cents, currency) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() })
}

function formatDate(seconds) {
  return new Date(seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

async function loadUpcomingInvoices() {
  const container = document.getElementById('billing-upcoming')
  if (!container) return

  try {
    const data = await stripeRequest('/stripe/upcoming_invoices')
    const previews = data.previews || []

    if (previews.length === 0) {
      container.innerHTML = '<p class="text-muted small mb-0">No active subscriptions.</p>'
      return
    }

    // The subscriptions list above says the same, next to each subscription's status.
    previews.filter(preview => preview.ends_at && preview.subscription_id).forEach(preview => {
      const selector = `.subscription-ends[data-stripe-subscription-id="${CSS.escape(preview.subscription_id)}"]`
      document.querySelectorAll(selector).forEach(badge => {
        badge.className = 'subscription-ends badge bg-warning text-dark ms-2'
        badge.textContent = `Ends ${formatDate(preview.ends_at)}`
      })
    })

    container.innerHTML = previews.map(preview => {
      const ending = preview.ends_at
        ? `<div class="alert alert-warning py-2 small mb-2">
             <strong>This subscription has been canceled and will end on ${formatDate(preview.ends_at)}.</strong>
             Usage until then is billed on the final invoice.
             To keep it, choose Keep subscription in its menu on the Subscriptions &amp; API Keys tab.
           </div>`
        : ''
      if (preview.error) {
        return `<div class="mb-3"><p class="mb-1"><strong>${escapeHtml(preview.plan_name)}</strong>: <span class="text-muted">${escapeHtml(preview.error)}</span></p>${ending}</div>`
      }
      const more = preview.more_lines
        ? '<p class="text-muted small mb-0">More lines are on the full invoice, under Manage subscription.</p>'
        : ''
      const rows = preview.lines.map(line => `
        <tr>
          <td>${escapeHtml(line.description)}</td>
          <td class="text-end">${formatMoney(line.amount, preview.currency)}</td>
        </tr>
      `).join('')
      return `
        <div class="mb-3">
          <p class="mb-1">
            <strong>${escapeHtml(preview.plan_name)}</strong>
            <span class="text-muted small ms-2">${formatDate(preview.period_start)} – ${formatDate(preview.period_end)}</span>
          </p>
          ${ending}
          <div class="table-responsive">
            <table class="table table-sm mb-0">
              <thead class="table-light"><tr><th>Item</th><th class="text-end">Amount</th></tr></thead>
              <tbody>${rows}</tbody>
              <tfoot><tr class="fw-bold"><td>Total so far</td><td class="text-end">${formatMoney(preview.total, preview.currency)}</td></tr></tfoot>
            </table>
          </div>
          ${more}
        </div>
      `
    }).join('')
  } catch (err) {
    showError(container, err.message)
  }
}

async function loadInvoices() {
  const container = document.getElementById('billing-invoices')
  if (!container) return

  try {
    const data = await stripeRequest('/stripe/invoices')
    const invoices = data.invoices

    if (!invoices || invoices.length === 0) {
      container.innerHTML = '<p class="text-muted small mb-0">No invoices yet.</p>'
      return
    }

    const rows = invoices.map(inv => {
      const dateStr = formatDate(inv.created)
      const amount = formatMoney(inv.amount_due, inv.currency)
      const statusBadge = inv.status === 'paid'
        ? '<span class="badge bg-success">Paid</span>'
        : inv.status === 'open'
          ? '<span class="badge bg-warning text-dark">Open</span>'
          : `<span class="badge bg-secondary">${inv.status}</span>`
      const pdfLink = inv.invoice_pdf
        ? `<a href="${inv.invoice_pdf}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-secondary"><i class="fas fa-download"></i></a>`
        : ''

      return `
        <tr>
          <td>${dateStr}</td>
          <td>${inv.number || '—'}</td>
          <td>${amount}</td>
          <td>${statusBadge}</td>
          <td>${pdfLink}</td>
        </tr>
      `
    }).join('')

    container.innerHTML = `
      <div class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Date</th>
              <th>Number</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `
  } catch (err) {
    showError(container, err.message)
  }
}

// ── Initialize on page load ──

function init() {
  initOrderFormStripe()
  initBillingTab()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
